/**
 * Users API client (A5 用户查询)
 *
 * Stream 2 Phase 2-4b / Plan §10 Stream 2 / Design §4.2 A5 + §5.7 boost 反查 unionId.
 *
 * 包装 axios apiClient 调用 admin-service /users 端点；返回类型与 admin-service
 * ServiceResponse 形状对齐。写动作通过 client.ts 自动注入 Idempotency-Key。
 *
 * V1 范围：
 *   - 用户搜索（q + searchBy）
 *   - 用户详情（base + counts + activeFreeze）
 *   - 6 个子资源：works / orders / dialogues / boost / shares / vouchers
 *   - 按 unionId 反查 boost 关系
 *   - 冻结 / 解冻写动作
 */
import { apiClient } from './client';

// ---------------------------------------------------------------------------
// Shared type
// ---------------------------------------------------------------------------

interface ServiceResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

// ---------------------------------------------------------------------------
// Domain types
// ---------------------------------------------------------------------------

export type UserStatus = 'active' | 'frozen' | 'recycling' | 'deleted';
export type SearchBy = 'userId' | 'phone' | 'nickname' | 'unionId';

export interface UserSearchItem {
  userId: string;
  nickname: string;
  phoneMasked: string;
  unionIdMasked: string;
  status: UserStatus;
  createdAt: string;
}

export interface SearchUsersResponse {
  items: UserSearchItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface UserBase {
  userId: string;
  nickname: string;
  phone: string;
  unionId: string;
  status: UserStatus;
  createdAt: string;
  updatedAt: string;
  recycleExpireAt: string | null;
}

export interface UserCounts {
  worksCount: number;
  ordersCount: number;
  vouchersCount: number;
  sharesCount: number;
  dialoguesCount: number;
}

export interface ActiveFreeze {
  freezeId: string;
  reason: string;
  frozenBy: string;
  canRevertUntil: string;
}

export interface GetUserResponse {
  user: UserBase;
  counts: UserCounts;
  activeFreeze: ActiveFreeze | null;
}

// Works
export interface WorkItem {
  workId: string;
  title: string;
  lifecycleStatus: string;
  isBookUnlocked: boolean;
  createdAt: string;
}

export interface GetUserWorksResponse {
  items: WorkItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface GetUserWorksFilter {
  page?: number;
  pageSize?: number;
  lifecycleStatus?: string;
}

// Orders
export interface UserOrderItem {
  orderId: string;
  userId: string;
  workId: string;
  totalAmount: number;
  status: string;
  paymentStatus: string;
  logisticsStatus: string;
  createdAt: string;
  updatedAt: string;
}

export interface GetUserOrdersResponse {
  items: UserOrderItem[];
  total: number;
  page: number;
  pageSize: number;
}

// Dialogues
export interface UtterancePreview {
  utteranceId: string;
  role: 'user' | 'ai';
  text: string;
  createdAt: string;
}

export interface RealtimeSession {
  sessionId: string;
  startedAt: string;
  endedAt: string | null;
}

export interface DialogueItem {
  dialogueId: string;
  workId: string;
  startedAt: string;
  endedAt: string | null;
  status: string;
  utteranceCount: number;
  realtimeSession: RealtimeSession | null;
  utterancesPreview: UtterancePreview[];
}

export interface GetUserDialoguesResponse {
  items: DialogueItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface GetUserDialoguesFilter {
  page?: number;
  pageSize?: number;
  workId?: string;
  fromDate?: string;
  toDate?: string;
}

// Boost
export interface BoostRel {
  relationId: string;
  inviterId: string;
  inviteeId: string;
  createdAt: string;
}

export interface BoostProg {
  progressId: string;
  userId: string;
  stage: string;
  completedAt: string | null;
}

export interface GetUserBoostResponse {
  relationships: BoostRel[];
  progress: BoostProg[];
}

// Shares
export interface ShareItem {
  shareId: string;
  workId: string;
  sharedAt: string;
  revokedAt: string | null;
  status: string;
}

export interface GetUserSharesResponse {
  items: ShareItem[];
}

// Vouchers / Coupons
export interface VoucherItem {
  voucherId: string;
  type: string;
  status: string;
  grantedAt: string;
  expireAt: string | null;
}

export interface CouponItem {
  couponId: string;
  code: string;
  status: string;
  usedAt: string | null;
  expireAt: string | null;
}

export interface GetUserVouchersResponse {
  vouchers: VoucherItem[];
  coupons: CouponItem[];
}

// Boost by unionId
export interface GetBoostByUnionIdResponse {
  relationships: BoostRel[];
  matchedInviteeCount: number;
}

// Freeze / Unfreeze
export interface FreezeUserInput {
  reason: string; // 5-200 chars
}

export interface FreezeUserResult {
  freezeId: string;
  canRevertUntil: string;
}

export interface UnfreezeUserInput {
  freezeId: string;
  reason: string; // 5-200 chars
}

// ---------------------------------------------------------------------------
// API functions (12 total)
// ---------------------------------------------------------------------------

/** GET /users/list — 全部用户列表（仅 super_admin；分页，按注册时间倒序，phone/unionId 脱敏）。 */
export async function listAllUsers(
  params: { page?: number; pageSize?: number } = {},
): Promise<SearchUsersResponse> {
  const p: Record<string, string | number> = {};
  if (params.page != null) p.page = params.page;
  if (params.pageSize != null) p.pageSize = params.pageSize;

  const resp = await apiClient.get<ServiceResponse<SearchUsersResponse>>('/users/list', {
    params: p,
  });
  return resp.data.data;
}

/** GET /users/search — 三源 join 搜索用户（q + searchBy + 分页）。 */
export async function searchUsers(params: {
  q: string;
  searchBy?: SearchBy;
  page?: number;
  pageSize?: number;
}): Promise<SearchUsersResponse> {
  const p: Record<string, string | number> = { q: params.q };
  if (params.searchBy) p.searchBy = params.searchBy;
  if (params.page != null) p.page = params.page;
  if (params.pageSize != null) p.pageSize = params.pageSize;

  const resp = await apiClient.get<ServiceResponse<SearchUsersResponse>>('/users/search', {
    params: p,
  });
  return resp.data.data;
}

/** GET /users/:userId — 用户详情（base + counts + activeFreeze）。 */
export async function getUser(userId: string): Promise<GetUserResponse> {
  const resp = await apiClient.get<ServiceResponse<GetUserResponse>>(
    `/users/${encodeURIComponent(userId)}`,
  );
  return resp.data.data;
}

/** GET /users/:userId/works — 用户作品列表（分页 + lifecycleStatus 过滤）。 */
export async function getUserWorks(
  userId: string,
  filter: GetUserWorksFilter = {},
): Promise<GetUserWorksResponse> {
  const p: Record<string, string | number> = {};
  if (filter.page != null) p.page = filter.page;
  if (filter.pageSize != null) p.pageSize = filter.pageSize;
  if (filter.lifecycleStatus) p.lifecycleStatus = filter.lifecycleStatus;

  const resp = await apiClient.get<ServiceResponse<GetUserWorksResponse>>(
    `/users/${encodeURIComponent(userId)}/works`,
    { params: p },
  );
  return resp.data.data;
}

/** GET /users/:userId/orders — 用户订单列表（分页）。 */
export async function getUserOrders(
  userId: string,
  filter: { page?: number; pageSize?: number } = {},
): Promise<GetUserOrdersResponse> {
  const p: Record<string, string | number> = {};
  if (filter.page != null) p.page = filter.page;
  if (filter.pageSize != null) p.pageSize = filter.pageSize;

  const resp = await apiClient.get<ServiceResponse<GetUserOrdersResponse>>(
    `/users/${encodeURIComponent(userId)}/orders`,
    { params: p },
  );
  return resp.data.data;
}

/** GET /users/:userId/dialogues — 用户讲述记录（分页 + workId + 时间范围）。含 utterancesPreview；无 PII 权限时 text 为占位字符串。 */
export async function getUserDialogues(
  userId: string,
  filter: GetUserDialoguesFilter = {},
): Promise<GetUserDialoguesResponse> {
  const p: Record<string, string | number> = {};
  if (filter.page != null) p.page = filter.page;
  if (filter.pageSize != null) p.pageSize = filter.pageSize;
  if (filter.workId) p.workId = filter.workId;
  if (filter.fromDate) p.fromDate = filter.fromDate;
  if (filter.toDate) p.toDate = filter.toDate;

  const resp = await apiClient.get<ServiceResponse<GetUserDialoguesResponse>>(
    `/users/${encodeURIComponent(userId)}/dialogues`,
    { params: p },
  );
  return resp.data.data;
}

/** GET /users/:userId/boost — 用户助力关系 + 进度。 */
export async function getUserBoost(userId: string): Promise<GetUserBoostResponse> {
  const resp = await apiClient.get<ServiceResponse<GetUserBoostResponse>>(
    `/users/${encodeURIComponent(userId)}/boost`,
  );
  return resp.data.data;
}

/** GET /users/:userId/shares — 用户分享全集。 */
export async function getUserShares(userId: string): Promise<GetUserSharesResponse> {
  const resp = await apiClient.get<ServiceResponse<GetUserSharesResponse>>(
    `/users/${encodeURIComponent(userId)}/shares`,
  );
  return resp.data.data;
}

/** GET /users/:userId/vouchers — 用户权益（vouchers + coupons 双表）。 */
export async function getUserVouchers(userId: string): Promise<GetUserVouchersResponse> {
  const resp = await apiClient.get<ServiceResponse<GetUserVouchersResponse>>(
    `/users/${encodeURIComponent(userId)}/vouchers`,
  );
  return resp.data.data;
}

/** GET /users/by-union-id/:unionId/boost — 按 unionId 反查 boost 关系（§5.7）。 */
export async function getBoostByUnionId(unionId: string): Promise<GetBoostByUnionIdResponse> {
  const resp = await apiClient.get<ServiceResponse<GetBoostByUnionIdResponse>>(
    `/users/by-union-id/${encodeURIComponent(unionId)}/boost`,
  );
  return resp.data.data;
}

/** POST /users/:userId/freeze — 冻结用户；reason 5-200 chars。 */
export async function freezeUser(userId: string, input: FreezeUserInput): Promise<FreezeUserResult> {
  const resp = await apiClient.post<ServiceResponse<FreezeUserResult>>(
    `/users/${encodeURIComponent(userId)}/freeze`,
    input,
  );
  return resp.data.data;
}

/** POST /users/:userId/unfreeze — 解冻用户；需 freezeId + reason（5-200 chars）。 */
export async function unfreezeUser(userId: string, input: UnfreezeUserInput): Promise<void> {
  await apiClient.post<ServiceResponse<null>>(
    `/users/${encodeURIComponent(userId)}/unfreeze`,
    input,
  );
}
